// src/lib/multipartUploader.ts
//
// Browser-side helper that uploads a single large File directly to S3 via
// per-part presigned PUT URLs. The flow is:
//   1. POST /multipart/initiate         → uploadId + s3Key + presigned part URLs
//   2. PUT  <presigned URL> per part    → ETag back from S3 (no auth header)
//   3. POST /multipart/complete         → backend stitches parts, writes File row
//
// Network-layer guarantees: each part is retried independently with
// exponential backoff. Aborting (via AbortSignal) cancels in-flight parts
// and best-effort tells S3 to discard the upload.

import {
  abortMultipartUpload,
  AdminFileUploadResDto,
  completeMultipartUpload,
  CompletedPart,
  initiateMultipartUpload,
  PresignedPart,
} from "./files.api"

export interface MultipartUploadOptions {
  /** Called with cumulative uploaded byte count after each part completes. */
  onProgress?: (uploadedBytes: number, totalBytes: number) => void
  /** Cancel the upload. In-flight PUTs abort and the backend is notified. */
  signal?: AbortSignal
  /** How many parts to PUT in parallel. S3 throughput scales linearly here. */
  concurrency?: number
  /** Per-part retry attempts on transient network failure. */
  retryAttempts?: number
}

const DEFAULT_CONCURRENCY = 4
const DEFAULT_RETRY_ATTEMPTS = 3
/** Initial backoff between part retries; doubled each attempt. */
const RETRY_BASE_DELAY_MS = 800

class AbortError extends Error {
  constructor() {
    super("upload aborted")
    this.name = "AbortError"
  }
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new AbortError())
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(new AbortError())
    }
    signal?.addEventListener("abort", onAbort, { once: true })
  })
}

/**
 * PUT a single part to its presigned URL, retrying on transient failures.
 * Returns the ETag header that S3 must echo back; if S3 omits it the part
 * is rejected (we cannot complete the upload without it).
 */
async function putPartWithRetry(
  url: string,
  body: Blob,
  partNumber: number,
  attempts: number,
  signal?: AbortSignal,
): Promise<string> {
  let lastErr: unknown
  for (let attempt = 1; attempt <= attempts; attempt++) {
    if (signal?.aborted) throw new AbortError()
    try {
      const response = await fetch(url, {
        method: "PUT",
        body,
        signal,
      })
      if (!response.ok) {
        throw new Error(`Part ${partNumber} PUT failed: HTTP ${response.status}`)
      }
      const etag = response.headers.get("ETag") ?? response.headers.get("etag")
      if (!etag) {
        // S3 always returns ETag; missing means CORS misconfig (ExposeHeaders).
        throw new Error(`Part ${partNumber}: ETag header missing (check S3 CORS ExposeHeaders)`)
      }
      return etag
    } catch (err) {
      if ((err as Error).name === "AbortError") throw err
      lastErr = err
      if (attempt < attempts) {
        await delay(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), signal)
      }
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(`Part ${partNumber} failed`)
}

/**
 * Best-effort browser-side video duration extraction.
 * Returns minutes (rounded down) or undefined if the file isn't a video,
 * the browser can't decode it, or the load times out.
 */
export async function getVideoDurationMinutes(file: File): Promise<number | undefined> {
  if (!file.type.startsWith("video/")) return undefined
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement("video")
    video.preload = "metadata"
    let settled = false
    const cleanup = () => {
      URL.revokeObjectURL(url)
      video.removeAttribute("src")
      video.load()
    }
    const finish = (value: number | undefined) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(value)
    }
    // Cap how long we wait for metadata so a corrupt/slow file doesn't block upload.
    const timer = setTimeout(() => finish(undefined), 5000)
    video.onloadedmetadata = () => {
      clearTimeout(timer)
      const seconds = Number.isFinite(video.duration) ? video.duration : 0
      finish(seconds > 0 ? Math.floor(seconds / 60) : undefined)
    }
    video.onerror = () => {
      clearTimeout(timer)
      finish(undefined)
    }
    video.src = url
  })
}

/**
 * Run `tasks` with at most `concurrency` in flight at any time.
 * Stops scheduling new tasks once any reject; in-flight tasks finish or abort.
 */
async function runWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  concurrency: number,
): Promise<T[]> {
  const results: T[] = new Array(tasks.length)
  let nextIndex = 0
  let firstError: unknown = null

  async function worker() {
    while (true) {
      if (firstError) return
      const i = nextIndex++
      if (i >= tasks.length) return
      try {
        results[i] = await tasks[i]()
      } catch (err) {
        if (!firstError) firstError = err
        return
      }
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, worker)
  await Promise.all(workers)
  if (firstError) throw firstError
  return results
}

/**
 * Upload a single File via S3 multipart presigned URLs.
 * Resolves with the FileResDto written to the DB by the /complete endpoint.
 * Rejects on AbortError when the caller cancels.
 */
export async function uploadFileMultipart(
  file: File,
  options: MultipartUploadOptions = {},
): Promise<AdminFileUploadResDto> {
  const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY
  const retryAttempts = options.retryAttempts ?? DEFAULT_RETRY_ATTEMPTS
  const { signal, onProgress } = options

  if (signal?.aborted) throw new AbortError()

  const durationMinutes = await getVideoDurationMinutes(file)
  if (signal?.aborted) throw new AbortError()

  const initiated = await initiateMultipartUpload({
    filename: file.name,
    mimeType: file.type || "application/octet-stream",
    size: file.size,
    durationMinutes,
  })

  const { uploadId, s3Key, partSize, parts } = initiated
  let uploadedBytes = 0

  const abortHandler = () => {
    void abortMultipartUpload({ uploadId, s3Key })
  }
  signal?.addEventListener("abort", abortHandler, { once: true })

  try {
    const tasks = parts.map((part: PresignedPart) => async (): Promise<CompletedPart> => {
      const start = (part.partNumber - 1) * partSize
      const end = Math.min(start + partSize, file.size)
      const blob = file.slice(start, end)
      const etag = await putPartWithRetry(part.url, blob, part.partNumber, retryAttempts, signal)
      uploadedBytes += blob.size
      onProgress?.(uploadedBytes, file.size)
      return { partNumber: part.partNumber, etag }
    })

    const completedParts = await runWithConcurrency(tasks, concurrency)

    if (signal?.aborted) throw new AbortError()

    return await completeMultipartUpload({
      uploadId,
      s3Key,
      originalName: file.name,
      mimeType: file.type || "application/octet-stream",
      size: file.size,
      durationMinutes,
      parts: completedParts,
    })
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      // abortHandler already fired via the signal listener.
    } else {
      // Unknown failure mid-upload — discard the half-finished S3 multipart.
      void abortMultipartUpload({ uploadId, s3Key })
    }
    throw err
  } finally {
    signal?.removeEventListener("abort", abortHandler)
  }
}

/** Files larger than this should use the multipart flow. */
export const MULTIPART_THRESHOLD_BYTES = 50 * 1024 * 1024
